'use client';

import { BlockRoot } from '@akademi/site-builder-runtime';
import { BRAND_NAME, PLANNER_HREF } from '../brand';
import { Icon } from '../shared/Icon';
import { NewsletterForm } from '../shared/NewsletterForm';
import { items, SkLink, text, type Localized } from '../shared/primitives';
import { useSavedItems } from '../shared/savedItems';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Site footer, per the site mockup: a dark band with the brand, tagline and
 * newsletter sign-up in the first column and up to four link columns beside it,
 * then the copyright and legal links. An optional saved-designs summary sits
 * under the newsletter when the shell configures one (`savedDesigns.title`).
 *
 * <p>Used two ways with the same props: as the chrome footer (built from the
 * shell's `footer` config by {@link footerPropsFromShell}), and as a block an
 * operator can place in a custom shell layout.
 *
 * <p>The saved-designs summary reads the ranges a visitor saved in this
 * browser. It renders its empty state on the server and fills in after
 * hydration, so it never causes a hydration mismatch.
 */
export interface FooterLinkItem {
  label?: Localized;
  href?: string;
}

export interface FooterColumn {
  title?: Localized;
  links?: FooterLinkItem[];
}

export interface FooterNewsletter {
  /** Enable only after the form and subscription handling are configured. */
  enabled?: boolean;
  title?: Localized;
  body?: Localized;
  label?: Localized;
  placeholder?: Localized;
  buttonLabel?: Localized;
  successMessage?: Localized;
  errorMessage?: Localized;
  note?: Localized;
  /** Key of the form in the admin form designer. */
  formKey?: string;
  /** Where the form goes when JavaScript is unavailable. */
  fallbackHref?: string;
}

export interface FooterSavedDesigns {
  title?: Localized;
  emptyBody?: Localized;
  /** Shown when ranges are saved; `{count}` is replaced with the number. */
  body?: Localized;
  linkLabel?: Localized;
  linkHref?: string;
}

export interface SpatialKitchenFooterProps {
  brandName?: Localized;
  tagline?: Localized;
  columns?: FooterColumn[];
  newsletter?: FooterNewsletter;
  savedDesigns?: FooterSavedDesigns;
  legalLinks?: FooterLinkItem[];
  /** `{year}` and `{brand}` are replaced. */
  copyright?: Localized;
  /** Render as the page's `contentinfo` landmark (chrome) or as a plain section (block). */
  asLandmark?: boolean;
}

const CSS = `
.sk-footer{--sk-footer-bg:#171B19;--sk-footer-text:#B9C1BC;--sk-footer-line:#2C322F;background:var(--sk-footer-bg);color:var(--sk-footer-text);padding-block:clamp(56px,6vw,72px) 2rem;font-size:.92rem}
.sk-footer__grid{display:grid;gap:2.25rem}
@media (min-width:1024px){.sk-footer__grid{grid-template-columns:minmax(0,1.5fr) minmax(0,4fr)}}
.sk-footer__columns{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:2rem 1.5rem}
@media (min-width:768px){.sk-footer__columns{grid-template-columns:repeat(var(--sk-footer-cols,4),minmax(0,1fr))}}
.sk-footer__brand{display:inline-flex;align-items:center;gap:.625rem;margin:0 0 .875rem;font-family:var(--sk-serif);font-weight:600;font-size:1.45rem;letter-spacing:-.02em;color:#fff}
.sk-footer__mark{width:32px;height:32px;border-radius:8px;background:var(--sk-sand);display:grid;place-items:center;flex:none}
.sk-footer__tagline{margin:0 0 1.25rem;max-width:28rem;color:var(--sk-footer-text)}
.sk-footer__heading{margin:0 0 1rem;font-family:var(--sk-sans);font-size:.78rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#fff}
.sk-footer__links{display:grid;gap:.5625rem}
.sk-footer a{color:var(--sk-footer-text);text-decoration:none}
.sk-footer a:hover{color:#fff;text-decoration:underline;text-underline-offset:.25em}
.sk-footer .sk-heading{color:#fff}
.sk-footer__side{display:grid;gap:1.5rem;align-content:start;margin-top:1.25rem}
.sk-footer__text{margin:0 0 .75rem;color:var(--sk-footer-text);font-size:.9rem}
.sk-footer__label{display:block;font-size:.85rem;font-weight:600;margin-bottom:.375rem;color:#fff}
.sk-newsletter__row{display:flex;flex-wrap:wrap;gap:.5rem}
.sk-newsletter__input{flex:1 1 12rem;min-width:0;padding:11px 16px;border:1px solid #6B746F;border-radius:999px;font:inherit;font-size:.92rem;color:#fff;background:#232926}
.sk-newsletter__input::placeholder{color:#9AA39E}
.sk-footer .sk-btn--primary{background:var(--sk-sand);color:var(--sk-ink);box-shadow:none}
.sk-footer .sk-btn--primary:hover{background:var(--sk-brass-soft);color:var(--sk-ink)}
.sk-footer__status{min-height:1.25rem;margin:.5rem 0 0;font-size:.85rem;color:#fff}
.sk-footer__note{margin:.25rem 0 0;font-size:.8rem;color:var(--sk-footer-text)}
.sk-footer__saved{padding-top:1.25rem;border-top:1px solid var(--sk-footer-line)}
.sk-footer__saved .sk-textlink{color:#fff}
.sk-saved__list{display:grid;gap:.375rem;margin:0 0 .75rem;padding:0;list-style:none}
.sk-footer__bottom{display:flex;flex-wrap:wrap;justify-content:space-between;gap:1rem 2rem;margin-top:clamp(2.5rem,5vw,3.5rem);padding-top:1.5rem;border-top:1px solid var(--sk-footer-line);font-size:.82rem}
.sk-footer__legal{display:flex;flex-wrap:wrap;gap:.5rem 1.25rem;margin:0;padding:0;list-style:none}
.sk-footer a:focus-visible,.sk-footer button:focus-visible,.sk-footer input:focus-visible{outline-color:#fff}
`;

function SavedSummary({ config }: { config: FooterSavedDesigns }) {
  const { saved } = useSavedItems();
  const count = saved.length;
  const title = text(config.title, 'Your saved designs');
  const body =
    count > 0
      ? text(config.body, 'Ranges saved on this device: {count}.').replace('{count}', String(count))
      : text(config.emptyBody, 'Tap the heart on any range to keep it here while you plan.');
  const linkLabel = text(config.linkLabel, 'Open Kitchen Designer');
  return (
    <div className="sk-footer__saved">
      <h2 className="sk-heading sk-footer__heading">{title}</h2>
      <p className="sk-footer__text" aria-live="polite">
        {body}
      </p>
      {count > 0 && (
        <ul className="sk-saved__list">
          {saved.slice(0, 3).map((item) => (
            <li key={item.id}>{item.href ? <SkLink href={item.href}>{item.name}</SkLink> : item.name}</li>
          ))}
        </ul>
      )}
      <SkLink href={config.linkHref || PLANNER_HREF} className="sk-textlink">
        {linkLabel}
        <Icon name="arrow-right" size={16} />
      </SkLink>
    </div>
  );
}

export function SpatialKitchenFooter(props: SpatialKitchenFooterProps) {
  const brand = text(props.brandName, BRAND_NAME);
  const columns = items<FooterColumn>(props.columns);
  const legal = items<FooterLinkItem>(props.legalLinks);
  const newsletter = props.newsletter ?? {};
  const year = new Date().getFullYear();
  const copyright = text(props.copyright, '© {year} {brand}')
    .replace('{year}', String(year))
    .replace('{brand}', brand);
  const Root = props.asLandmark === false ? 'section' : 'footer';

  return (
    <BlockRoot
      as={Root}
      className="sk sk-footer"
      aria-label={props.asLandmark === false ? `${brand} footer` : undefined}
      style={{ ['--sk-footer-cols' as string]: String(Math.max(1, Math.min(columns.length, 4))) }}
    >
      <SkBaseStyles />
      <SkStyles id="footer" css={CSS} />
      <div className="sk-container">
        <div className="sk-footer__grid">
          <div>
            <p className="sk-footer__brand">
              <span className="sk-footer__mark" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.2} focusable="false">
                  <rect x="4" y="4" width="7" height="16" rx="1" />
                  <rect x="13" y="4" width="7" height="7" rx="1" />
                  <rect x="13" y="13" width="7" height="7" rx="1" />
                </svg>
              </span>
              {brand}
            </p>
            {text(props.tagline) && <p className="sk-footer__tagline">{text(props.tagline)}</p>}
            <div className="sk-footer__side">
              {newsletter.enabled !== false && (
                <NewsletterForm
                  title={text(newsletter.title, 'Ideas for your kitchen, now and then')}
                  body={text(newsletter.body)}
                  label={text(newsletter.label, 'Email address')}
                  placeholder={text(newsletter.placeholder)}
                  buttonLabel={text(newsletter.buttonLabel, 'Sign up')}
                  successMessage={text(newsletter.successMessage, 'Thank you. You are on the list.')}
                  errorMessage={text(newsletter.errorMessage, 'That did not go through. Please try again.')}
                  note={text(newsletter.note)}
                  formKey={newsletter.formKey || 'newsletter'}
                  fallbackHref={newsletter.fallbackHref || '/contact'}
                />
              )}
              {text(props.savedDesigns?.title) && <SavedSummary config={props.savedDesigns ?? {}} />}
            </div>
          </div>
          <nav aria-label="Footer" className="sk-footer__columns">
            {columns.map((column, index) => (
              <div key={`${text(column.title)}-${index}`}>
                <h2 className="sk-heading sk-footer__heading">{text(column.title)}</h2>
                <ul className="sk-footer__links sk-list-reset">
                  {items<FooterLinkItem>(column.links).map((link, linkIndex) => (
                    <li key={`${link.href}-${linkIndex}`}>
                      <SkLink href={link.href}>{text(link.label)}</SkLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
        <div className="sk-footer__bottom">
          <p style={{ margin: 0 }}>{copyright}</p>
          {legal.length > 0 && (
            <ul className="sk-footer__legal" aria-label="Legal">
              {legal.map((link, index) => (
                <li key={`${link.href}-${index}`}>
                  <SkLink href={link.href}>{text(link.label)}</SkLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </BlockRoot>
  );
}
